import { useNavigate } from 'react-router-dom';

import type { LinkedProfileTicketSummary } from '../api/types';

const STATUS_COLOR: Record<string, string> = {
  OPEN: '#22c55e',
  TO_DO: '#22c55e',
  IN_PROGRESS: '#818cf8',
  PENDING: '#f59e0b',
  ON_HOLD: '#f59e0b',
  WAITING_FOR_MORE_INFO: '#f59e0b',
  WAITING_FOR_CONFIRMATION: '#f59e0b',
  DEFERRED: '#f59e0b',
  RESOLVED: '#475569',
  CLOSED: '#334155',
  COMPLETED: '#475569',
  CANNOT_RESOLVE: '#334155',
};

type Props = {
  ticket: LinkedProfileTicketSummary;
  currentTicketId?: string;
  showCurrentLabel?: boolean;
  onNavigate?: () => void;
};

export default function ProfileTicketRow({
  ticket,
  currentTicketId,
  showCurrentLabel = false,
  onNavigate,
}: Props) {
  const navigate = useNavigate();
  const isCurrentTicket = Boolean(currentTicketId && ticket.id === currentTicketId);
  const isInteractive = !isCurrentTicket;
  const statusCode = ticket.status.code;

  function goToTicket() {
    if (!isInteractive) return;
    navigate(`/tickets/${ticket.id}`);
    onNavigate?.();
  }

  return (
    <div
      className={`profile-ticket-row${isCurrentTicket ? ' profile-ticket-row--current' : ''}`}
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onClick={goToTicket}
      onKeyDown={(e) => {
        if (!isInteractive) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          goToTicket();
        }
      }}
    >
      <div className="profile-ticket-row-top">
        <span className="profile-ticket-row-number">{ticket.ticketNumber}</span>
        <span
          className="profile-ticket-row-status"
          style={{ color: STATUS_COLOR[statusCode] ?? '#94a3b8' }}
        >
          {ticket.status.name || statusCode.replace(/_/g, ' ')}
        </span>
      </div>
      <div className="profile-ticket-row-title">
        {ticket.title}
        {showCurrentLabel && isCurrentTicket && (
          <span className="profile-ticket-row-current-tag"> (current)</span>
        )}
      </div>
      {ticket.assignedAgent && (
        <div className="profile-ticket-row-agent">{ticket.assignedAgent.name}</div>
      )}
    </div>
  );
}

function ProfileTicketRowSkeleton() {
  return (
    <div className="profile-ticket-row profile-ticket-row--skeleton" aria-hidden>
      <div className="profile-ticket-skeleton-line profile-ticket-skeleton-line--short" />
      <div className="profile-ticket-skeleton-line profile-ticket-skeleton-line--long" />
      <div className="profile-ticket-skeleton-line profile-ticket-skeleton-line--medium" />
    </div>
  );
}

export function ProfileTicketRowsSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div className="profile-ticket-skeleton-list">
      {Array.from({ length: count }, (_, i) => (
        <ProfileTicketRowSkeleton key={i} />
      ))}
    </div>
  );
}
